import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Save as SaveIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  GpsFixed as GpsFixedIcon
} from '@mui/icons-material';
import { fetchJson } from '../../utils/fetchJson';
import { newIdempotencyKey } from '../../utils/outboxIdentity';
import useScopeAutoRefresh from '../../hooks/useScopeAutoRefresh';
import getScopeHeaders from '../../utils/scopeHeaders';
import { useTranslation } from 'react-i18next';
import {
  haversineDistance,
  geofenceVerdict,
  geofenceAllows,
  DEFAULT_GEOFENCE_RADIUS,
  FIX_FRESH_MS,
} from '../../utils/geofence';
// `fixIsGoodEnough` and `betterFix` are gone from here on purpose: they are
// sampling decisions, and this page inlining them is what produced a register
// refused on the strength of one cached wifi lookup. They live in the shared
// capture routine now, which browserLocation drives.
import { bestPositionFix, geolocationAvailable } from '../../utils/browserLocation';

const ATTENDANCE_STATUS = {
  PRESENT: 'present',
  ABSENT: 'absent',
  LATE: 'late',
};

const STATUS_COLORS = {
  present: 'success',
  absent: 'error',
  late: 'warning',
};

const ATTENDANCE_TIME_ZONE = (
  process.env.REACT_APP_ATTENDANCE_TIME_ZONE ||
  process.env.REACT_APP_APP_TIME_ZONE ||
  'Africa/Douala'
);

const toLocalDateOnly = (value) => {
  if (!value) return '';
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getAttendanceZonedParts = (value = new Date()) => {
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: ATTENDANCE_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    });
    const parts = formatter.formatToParts(date);
    const out = {};
    for (const part of parts) {
      if (part?.type && part.type !== 'literal') out[part.type] = part.value;
    }
    if (!out.year || !out.month || !out.day) return null;
    return out;
  } catch (_) {
    return null;
  }
};

const getAttendanceTodayDateOnly = (value = new Date()) => {
  const parts = getAttendanceZonedParts(value);
  if (parts?.year && parts?.month && parts?.day) {
    return `${parts.year}-${parts.month}-${parts.day}`;
  }
  return toLocalDateOnly(value);
};

const getAttendanceCurrentMinutes = (value = new Date()) => {
  const parts = getAttendanceZonedParts(value);
  const hours = Number(parts?.hour);
  const minutes = Number(parts?.minute);
  if (Number.isFinite(hours) && Number.isFinite(minutes)) {
    return (hours * 60) + minutes;
  }
  const now = value instanceof Date ? value : new Date(value);
  return (now.getHours() * 60) + now.getMinutes();
 };

export default function AttendancePage() {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionResolveError, setSessionResolveError] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentEvaluation, setCurrentEvaluation] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [lateMinutesMap, setLateMinutesMap] = useState({});
  const [periods, setPeriods] = useState(1);
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [resolvedPeriod, setResolvedPeriod] = useState('');
  const [resolvedSession, setResolvedSession] = useState(null);
  const [sessionOutsideWindow, setSessionOutsideWindow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [timetableEntries, setTimetableEntries] = useState([]);
  const [allowedDays, setAllowedDays] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());
  const [schoolGeo, setSchoolGeo] = useState(null);
  // 'uncertain' is a real outcome, not a variant of 'outside': the fix's error
  // circle straddles the fence, so it cannot say which side the teacher is on.
  //
  // `inside`, not `within`. The phone reports the shared verdict verbatim and
  // this page used to rename it, so the two screens could not be compared
  // against each other or asserted on by one conformance script.
  const [geoStatus, setGeoStatus] = useState('idle'); // idle | checking | inside | uncertain | outside | unavailable | error
  const [geoDistance, setGeoDistance] = useState(null);
  // Kept in state so the panel can show the fix tightening while it settles,
  // rather than sitting on "checking" for the whole silent window.
  const [geoAccuracy, setGeoAccuracy] = useState(null);
  // The whole reading, so the panel can print the teacher's own latitude and
  // longitude beside the school's. Without both pairs on screen there is no way
  // to tell a teacher in the wrong place from a school pinned in the wrong
  // place, which is exactly the question that kept going unanswered.
  const [geoFix, setGeoFix] = useState(null);

  const todayStr = getAttendanceTodayDateOnly(nowTick);
  const selectedDateStr = toLocalDateOnly(selectedDate);
  const isSameDaySelection = selectedDateStr === todayStr;
  const allowedDaysSet = React.useMemo(() => new Set((allowedDays || []).map(String)), [allowedDays]);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  // Fetch school geolocation settings once on mount
  useEffect(() => {
    (async () => {
      try {
        const geo = await fetchJson('/teacher/school-geolocation');
        setSchoolGeo(geo || null);
      } catch (_) {
        setSchoolGeo(null);
      }
    })();
  }, []);

  /**
   * MEASURE WHERE THE TEACHER IS, and say so.
   *
   * THIS FILE IS WHERE THE 1KM BUG LIVED. It used to inline its own sampling
   * loop, and that loop called `fixIsGoodEnough` on the very FIRST reading with
   * no two-sample floor. That function returns true outright for any accuracy
   * under 30m — and a browser's first answer is a wifi/cell-tower lookup that
   * routinely claims ±20-25m while being hundreds of metres out. So the window
   * closed on sample one, at the wrong place, and the teacher was refused. Not
   * intermittently: every time.
   *
   * It also ran an 8000ms window against its own `timeout: 15000`, so the two
   * timers disagreed and a slow device could conclude with nothing at all.
   *
   * The loop is gone. `bestPositionFix` is the same routine the phone runs,
   * through the same shared reducer, and `tools/geofence-conformance.mjs`
   * replays a scripted "cached wifi estimate, then real GPS" sequence through
   * both copies to prove it.
   *
   * Returns the verdict rather than only setting state — see the note at the
   * call site in saveAttendance about why reading it back was broken.
   */
  const measure = useCallback(async () => {
    const radius = schoolGeo?.geofenceRadius || DEFAULT_GEOFENCE_RADIUS;
    // `== null`, not falsy. A school on the equator or the Greenwich meridian
    // has a legitimate coordinate of 0, and a falsy test silently turned the
    // fence off for it.
    if (!schoolGeo?.enforceGeofence || schoolGeo.latitude == null || schoolGeo.longitude == null) {
      setGeoStatus('idle');
      return { allowed: true, verdict: 'unenforced' };
    }
    if (!geolocationAvailable()) {
      setGeoStatus('unavailable');
      return { allowed: false, verdict: 'unavailable' };
    }

    setGeoStatus('checking');
    const lat = Number(schoolGeo.latitude);
    const lon = Number(schoolGeo.longitude);

    try {
      const fix = await bestPositionFix(
        { radius, latitude: lat, longitude: lon },
        // Wired, unlike before: the panel tightens as readings improve instead
        // of sitting silent for the whole window.
        (partial) => setGeoAccuracy(Math.round(partial.accuracy || 0)),
      );
      const distance = haversineDistance(fix.latitude, fix.longitude, lat, lon);
      const { verdict } = geofenceVerdict({ distance, accuracy: fix.accuracy, radius });

      // The pin it was measured AGAINST is stamped on, so a reading cannot be
      // reused after an administrator moves the school — see positionForFiling.
      setGeoFix({ ...fix, pinLat: lat, pinLon: lon, pinRadius: radius });
      setGeoDistance(Math.round(distance));
      setGeoAccuracy(Math.round(fix.accuracy || 0));
      setGeoStatus(verdict);

      return {
        allowed: geofenceAllows(verdict), verdict, distance: Math.round(distance),
        accuracy: Math.round(fix.accuracy || 0), radius, fix,
      };
    } catch (_) {
      setGeoStatus('error');
      setGeoFix(null);
      return { allowed: false, verdict: 'error', radius };
    }
  }, [schoolGeo]);

      // GPS converges: the first reading is the worst one. Watch for a few
  /**
   * The reading the register is actually filed against.
   *
   * Reuses the panel's fix when it is still FRESH, and re-measures through the
   * same `measure()` when it is not — so a second window is never served
   * silently behind a spinning Save button.
   *
   * Fresh means all three: taken within FIX_FRESH_MS, measured against the pin
   * currently in force, and `settled` — a reading that merely ran out of window
   * is not a basis for a permanent record. Filling forty pupils takes two to
   * four minutes, so the common path DOES re-measure, which is right: what gets
   * recorded must be where the teacher was when they filed, not where they were
   * when they picked the subject.
   */
  const positionForFiling = useCallback(async () => {
    if (!schoolGeo?.enforceGeofence || schoolGeo.latitude == null || schoolGeo.longitude == null) {
      return { allowed: true, verdict: 'unenforced' };
    }
    const radius = schoolGeo.geofenceRadius || DEFAULT_GEOFENCE_RADIUS;
    const fresh = geoFix
      && geoFix.settled
      && Date.now() - geoFix.capturedAt <= FIX_FRESH_MS
      && geoFix.pinLat === Number(schoolGeo.latitude)
      && geoFix.pinLon === Number(schoolGeo.longitude)
      && geoFix.pinRadius === radius;

    if (fresh) {
      const distance = haversineDistance(
        geoFix.latitude, geoFix.longitude, Number(schoolGeo.latitude), Number(schoolGeo.longitude),
      );
      const { verdict } = geofenceVerdict({ distance, accuracy: geoFix.accuracy, radius });
      return {
        allowed: geofenceAllows(verdict), verdict, distance: Math.round(distance),
        accuracy: Math.round(geoFix.accuracy || 0), radius, fix: geoFix,
      };
    }
    return measure();
  }, [schoolGeo, geoFix, measure]);

  /**
   * READ THE POSITION AUTOMATICALLY, as soon as there is something to measure.
   *
   * This page used to measure ONLY inside saveAttendance, so a teacher had no
   * way to see where they stood until they had filled in the whole register and
   * pressed Save — at which point the answer was a refusal with no numbers to
   * argue with. The phone has always measured as soon as a class and subject
   * are chosen; this is the same trigger, so the two screens behave alike.
   */
  useEffect(() => {
    if (!schoolGeo?.enforceGeofence) return;
    if (!selectedClass || !selectedSubject) return;
    measure();
  }, [measure, schoolGeo, selectedClass, selectedSubject]);

  const getRequestErrorMessage = (err, fallback = '') => {
    const backendMessage = err?.data?.userMessage || err?.data?.publicMessage || err?.data?.error || err?.data?.message;
    if (typeof backendMessage === 'string' && backendMessage.trim()) return backendMessage.trim();
    if (typeof err?.userMessage === 'string' && err.userMessage.trim()) return err.userMessage.trim();
    return fallback;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dateStr = toLocalDateOnly(selectedDate || new Date());
        const qs = new URLSearchParams({ date: dateStr });
        if (selectedClass) qs.set('classId', String(selectedClass));
        const data = await fetchJson(`/api/attendance/current-evaluation?${qs.toString()}`, { headers: getScopeHeaders() });
        if (cancelled) return;
        setCurrentEvaluation(data?.evaluation || null);
      } catch (_) {
        if (!cancelled) setCurrentEvaluation(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate, selectedClass]);

  const getWeekdayName = (dt) => {
    if (!dt) return '';
    try {
      return dt.toLocaleDateString('en-US', { weekday: 'long' });
    } catch (_) {
      return '';
    }
  };

  const getTimetableDayValue = (dt) => {
    if (!dt) return '';
    try {
      const jsDay = dt.getDay();
      const dayNum = jsDay === 0 ? 7 : jsDay;
      return String(dayNum);
    } catch (_) {
      return '';
    }
  };

  const TIMETABLE_DAY_NUMBER_BY_TOKEN = {
    '1': '1',
    monday: '1',
    mon: '1',
    '2': '2',
    tuesday: '2',
    tue: '2',
    tues: '2',
    '3': '3',
    wednesday: '3',
    wed: '3',
    '4': '4',
    thursday: '4',
    thu: '4',
    thur: '4',
    thurs: '4',
    '5': '5',
    friday: '5',
    fri: '5',
    '6': '6',
    saturday: '6',
    sat: '6',
    '7': '7',
    '0': '7',
    sunday: '7',
    sun: '7',
  };

  const normalizeTimetableDayToken = (value) => {
    if (value == null) return '';
    const raw = String(value).trim();
    if (!raw) return '';
    return TIMETABLE_DAY_NUMBER_BY_TOKEN[raw.toLowerCase()] || raw;
  };

  const getNextTimetableDayToken = (value) => {
    const normalized = normalizeTimetableDayToken(value);
    const n = Number(normalized);
    if (!Number.isInteger(n) || n < 1 || n > 7) return '';
    return String(n === 7 ? 1 : n + 1);
  };

  const timeStringToMinutes = (timeValue) => {
    if (!timeValue) return null;
    const raw = String(timeValue);
    const [h, m] = raw.split(':');
    const hh = parseInt(h, 10);
    const mm = parseInt(m, 10);
    if (!Number.isInteger(hh) || !Number.isInteger(mm)) return null;
    return hh * 60 + mm;
  };

  const minutesToHHMM = (minutes) => {
    const total = Number(minutes);
    if (!Number.isFinite(total)) return '';
    const hh = Math.floor(total / 60);
    const mm = total % 60;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };

  const formatMinutesTo12h = (minutes) => {
    const total = Number(minutes);
    if (!Number.isFinite(total)) return '';
    const hh = Math.floor(total / 60) % 24;
    const mm = total % 60;
    const period = hh >= 12 ? 'pm' : 'am';
    const h = hh % 12 || 12;
    return `${h}:${String(mm).padStart(2, '0')}${period}`;
  };

  const sessionWindow = React.useMemo(() => {
    if (!resolvedSession) return null;
    const startMin = timeStringToMinutes(resolvedSession.activeWindowStartTime || resolvedSession.startTime);
    const endMin = timeStringToMinutes(resolvedSession.activeWindowEndTime || resolvedSession.endTime);
    if (startMin == null || endMin == null) return null;
    const grace = Number(resolvedSession.graceMinutes) || 10;
    return {
      startMin,
      endMin,
      graceEndMin: endMin + grace,
      label: resolvedSession.period || `${resolvedSession.startTime}-${resolvedSession.endTime}`
    };
  }, [resolvedSession]);

  const canRecordAttendance = React.useMemo(() => {
    if (!selectedClass || !selectedSubject || !selectedDate) return false;
    if (!isSameDaySelection) return false;
    if (!sessionWindow) return false;
    const nowMinutes = getAttendanceCurrentMinutes(nowTick);
    return nowMinutes >= sessionWindow.startMin && nowMinutes <= sessionWindow.graceEndMin;
  }, [selectedClass, selectedSubject, selectedDate, isSameDaySelection, sessionWindow, nowTick]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedClass || !selectedSubject || !selectedDate) {
        setResolvedSession(null);
        setResolvedPeriod('');
        setDurationMinutes(0);
        setSessionResolveError('');
        setSessionOutsideWindow(false);
        return;
      }
      if (!isSameDaySelection) {
        setResolvedSession(null);
        setResolvedPeriod('');
        setDurationMinutes(0);
        setSessionResolveError('');
        setSessionOutsideWindow(false);
        return;
      }
      try {
        const dateStr = toLocalDateOnly(selectedDate);
        const res = await fetchJson(`/api/attendance/resolve-session?classId=${encodeURIComponent(selectedClass)}&subjectId=${encodeURIComponent(selectedSubject)}&date=${encodeURIComponent(dateStr)}`);
        if (cancelled) return;
        setResolvedSession(res || null);
        setResolvedPeriod(res?.period || '');
        setPeriods(Number(res?.periods) || 1);
        setDurationMinutes(Number(res?.durationMinutes) || 0);
        setSessionResolveError('');
        setSessionOutsideWindow(false);
      } catch (err) {
        if (cancelled) return;
        setResolvedSession(null);
        setResolvedPeriod('');
        setDurationMinutes(0);
        const httpStatus = err?.response?.status;
        if (httpStatus === 409) {
          // Outside the time window — not an error, just not open yet or already closed
          setSessionOutsideWindow(true);
          setSessionResolveError('');
        } else {
          setSessionOutsideWindow(false);
          setSessionResolveError(getRequestErrorMessage(err, t('teacherAttendancePage.errors.noTimetableSlot')));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedClass, selectedSubject, selectedDate, isSameDaySelection]);

  useEffect(() => {
    // Wrap to avoid accessing loadClasses before initialization
    (async () => { await loadClasses(); })();
  }, []);

  // Auto-refresh when AY/phase changes
  useScopeAutoRefresh([() => loadClasses(), () => {
    if (selectedClass) {
      loadSubjects();
      loadStudents();
      if (selectedSubject && resolvedPeriod) loadAttendance();
    }
  }]);

  // Re-fetch classes when window regains focus (admin may have assigned new subjects)
  useEffect(() => {
    const onFocus = () => loadClasses();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  useEffect(() => {
    if (selectedClass) {
      loadSubjects();
      loadStudents();
      // reset subject when class changes
      setSelectedSubject('');
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedClass) {
      loadStudents();
    }
  }, [selectedClass, selectedSubject, selectedDate]);

  useEffect(() => {
    if (selectedClass && selectedSubject && resolvedPeriod) {
      loadAttendance();
    } else if (selectedClass && selectedSubject) {
      const emptyMap = {};
      students.forEach(s => { emptyMap[s.id] = ATTENDANCE_STATUS.PRESENT; });
      setAttendance(emptyMap);
      setLateMinutesMap({});
    }
  }, [selectedClass, selectedSubject, selectedDate, resolvedPeriod]);

  // Initialize empty attendance map when students list changes and no attendance loaded yet
  useEffect(() => {
    if (!students.length) return;
    setAttendance(prev => {
      const hasExisting = Object.keys(prev).length > 0;
      if (hasExisting) {
        // Merge new students into existing map without overwriting
        const next = { ...prev };
        students.forEach(s => {
          if (!(s.id in next)) next[s.id] = ATTENDANCE_STATUS.PRESENT;
        });
        return next;
      }
      const map = {};
      students.forEach(s => { map[s.id] = ATTENDANCE_STATUS.PRESENT; });
      return map;
    });
  }, [students]);

  useEffect(() => {
    if (selectedClass && selectedSubject) {
      loadSubjectTimetable();
      const id = setInterval(loadSubjectTimetable, 30000);
      return () => clearInterval(id);
    } else {
      setTimetableEntries([]);
      setAllowedDays([]);
    }
  }, [selectedClass, selectedSubject]);

  useEffect(() => {
    if (!resolvedSession) return;
    setPeriods(Number(resolvedSession?.periods) || 1);
    setDurationMinutes(Number(resolvedSession?.durationMinutes) || 0);
  }, [resolvedSession]);

  const loadClasses = async () => {
    try {
      const response = await fetchJson(`/teacher/classes?_t=${Date.now()}`);
      const classData = Array.isArray(response)
        ? response
        : (response?.classes || response?.data || []);
      setClasses(classData);
      setSelectedClass((prev) => {
        if (prev && classData.some((row) => String(row?.id) === String(prev))) return prev;
        return classData[0]?.id ? String(classData[0].id) : '';
      });
    } catch (err) {
      console.error('Failed to load classes:', err);
      setError(t('teacherAttendancePage.errors.loadClasses'));
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async () => {
    try {
      const res = await fetchJson(`/teacher/classes/${selectedClass}/subjects?requireTimetable=1`);
      const data = Array.isArray(res) ? res : (res?.data || res?.subjects || []);
      setSubjects(data);
      setSelectedSubject((prev) => {
        if (prev && data.some((row) => String(row?.id || row?.subjectId) === String(prev))) return prev;
        const nextId = data[0]?.id || data[0]?.subjectId || '';
        return nextId ? String(nextId) : '';
      });
    } catch (err) {
      console.error('Failed to load subjects:', err);
      setSubjects([]);
      setSelectedSubject('');
    }
  };

  const loadSubjectTimetable = async () => {
    if (!selectedClass || !selectedSubject) {
      setTimetableEntries([]);
      setAllowedDays([]);
      return;
    }
    try {
      const res = await fetchJson(`/teacher/timetable?classId=${encodeURIComponent(selectedClass)}&subjectId=${encodeURIComponent(selectedSubject)}&_t=${Date.now()}`);
      const entries = Array.isArray(res) ? res : (res?.data || res?.entries || []);
      setTimetableEntries(entries);
      const daySet = new Set();
      entries.forEach((entry) => {
        const rawDay = entry?.day ?? entry?.dayOfWeek;
        const normalizedDay = normalizeTimetableDayToken(rawDay);
        if (normalizedDay) daySet.add(String(normalizedDay));
        const startMin = timeStringToMinutes(entry?.startTime);
        const endMin = timeStringToMinutes(entry?.endTime);
        if (normalizedDay && startMin != null && endMin != null && endMin <= startMin) {
          const nextDay = getNextTimetableDayToken(normalizedDay);
          if (nextDay) daySet.add(nextDay);
        }
      });
      const days = [...daySet];
      setAllowedDays(days);
    } catch (err) {
      console.error('Failed to load subject timetable:', err);
      setTimetableEntries([]);
      setAllowedDays([]);
    }
  };

  const loadStudents = async () => {
    try {
      // Do not show student list when there's no active timetable slot at the current moment.
      // The session window is derived from /api/attendance/resolve-session.
      if (selectedClass && selectedSubject && isSameDaySelection && !sessionWindow) {
        setStudents([]);
        setError(null);
        return;
      }

      if (selectedClass && selectedSubject && selectedDate) {
        const dateStr = toLocalDateOnly(selectedDate);
        const res = await fetchJson(`/api/attendance/eligible-students?classId=${encodeURIComponent(selectedClass)}&subjectId=${encodeURIComponent(selectedSubject)}&date=${encodeURIComponent(dateStr)}`);
        const studentData = Array.isArray(res) ? res : (res?.students || res?.data || []);
        setStudents(studentData);
        setError(null);
        return;
      }

      const response = await fetchJson(`/teacher/classes/${selectedClass}/students`);
      const studentData = response?.students || response?.data || [];
      setStudents(studentData);
      setError(null);
    } catch (err) {
      console.error('Failed to load students:', err);
      setError(t('teacherAttendancePage.errors.loadStudents'));
    }
  };

  const loadAttendance = async () => {
    if (!selectedClass || !selectedSubject || !selectedDate || !resolvedPeriod) {
      const emptyMap = {};
      students.forEach(s => { emptyMap[s.id] = ATTENDANCE_STATUS.PRESENT; });
      setAttendance(emptyMap);
      setLateMinutesMap({});
      return;
    }
    try {
      const dateStr = toLocalDateOnly(selectedDate);
      const qs = new URLSearchParams();
      qs.set('classId', selectedClass);
      qs.set('subjectId', selectedSubject);
      qs.set('startDate', dateStr);
      qs.set('endDate', dateStr);
      if (resolvedPeriod) qs.set('period', resolvedPeriod);
      const response = await fetchJson(`/api/attendance?${qs.toString()}`);
      const records = response?.attendance || response?.data || [];
      const map = {};
      const lateMap = {};
      students.forEach(s => { map[s.id] = ATTENDANCE_STATUS.PRESENT; });
      records.forEach(rec => {
        const sid = rec.studentId || rec.student?.id;
        if (sid) {
          map[sid] = rec.status;
          if (rec.status === ATTENDANCE_STATUS.LATE) {
            lateMap[sid] = Number(rec.lateMinutes) || 1;
          }
        }
      });
      setAttendance(map);
      setLateMinutesMap(lateMap);
    } catch (err) {
      console.error('Failed to load attendance:', err);
      const emptyMap = {};
      students.forEach(s => { emptyMap[s.id] = ATTENDANCE_STATUS.PRESENT; });
      setAttendance(emptyMap);
      setLateMinutesMap({});
    }
  };

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({
      ...prev,
      [studentId]: status
    }));
    if (status !== ATTENDANCE_STATUS.LATE) {
      setLateMinutesMap(prev => ({ ...prev, [studentId]: undefined }));
    } else {
      setLateMinutesMap(prev => ({ ...prev, [studentId]: prev[studentId] ?? 1 }));
    }
  };

  const handleLateMinutesChange = (studentId, value) => {
    const v = parseInt(value || '0', 10);
    setLateMinutesMap(prev => ({ ...prev, [studentId]: isNaN(v) ? undefined : v }));
  };

  const saveAttendance = async () => {
    if (!selectedClass || !selectedSubject || !selectedDate) return;
    try {
      setSaving(true);

      // GPS geofence check (non-disruptive: only blocks if enforceGeofence is on)
      //
      // THE RESULT IS READ FROM THE PROMISE, not from state. This used to check
      // `geoStatus` on the line after the call that set it — but a setState does
      // not update the value already captured in this closure, so the branch
      // always read the PREVIOUS render's status. The teacher who was 940m away
      // got "could not verify your location" instead of a distance, which is
      // both useless and the reason this bug went so long without a report that
      // named it.
      //
      // A recent reading is reused rather than measured again: see
      // `positionForFiling`.
      const geo = await positionForFiling();
      if (!geo.allowed) {
        const radius = geo.radius || schoolGeo?.geofenceRadius || DEFAULT_GEOFENCE_RADIUS;
        if (geo.verdict === 'outside') {
          setError(t('teacherAttendancePage.errors.geoOutside', { distance: geo.distance ?? '?', radius }));
        } else if (geo.verdict === 'unavailable') {
          setError(t('teacherAttendancePage.errors.geoUnavailable'));
        } else {
          setError(t('teacherAttendancePage.errors.geoVerifyFailed'));
        }
        setSaving(false);
        return;
      }

      const dateStr = toLocalDateOnly(selectedDate);
      if (!isSameDaySelection) {
        setError(t('teacherAttendancePage.errors.mustSelectToday'));
        setSaving(false);
        return;
      }
      // Enforce timetable day-of-week alignment for validity
      if (allowedDaysSet.size) {
        const dayName = getWeekdayName(selectedDate);
        const dayValue = getTimetableDayValue(selectedDate);
        if (!allowedDaysSet.has(String(dayValue)) && !allowedDaysSet.has(String(dayName))) {
          setError(t('teacherAttendancePage.errors.unscheduledDay'));
          setSaving(false);
          return;
        }
      }
      if (!sessionWindow || !resolvedPeriod) {
        setError(sessionResolveError || t('teacherAttendancePage.errors.noTimetableSlot'));
        setSaving(false);
        return;
      }
      const nowMinutes = getAttendanceCurrentMinutes(nowTick);
      if (nowMinutes < sessionWindow.startMin || nowMinutes > sessionWindow.graceEndMin) {
        setError(t('teacherAttendancePage.errors.recordWindow', { start: formatMinutesTo12h(sessionWindow.startMin), end: formatMinutesTo12h(sessionWindow.graceEndMin) }));
        setSaving(false);
        return;
      }
      // basic client-side validation for late minutes
      const sessionMinutes = Number(durationMinutes) || 0;
      for (const [sid, status] of Object.entries(attendance)) {
        if (status === ATTENDANCE_STATUS.LATE) {
          const lm = Number(lateMinutesMap[sid]);
          if (!Number.isInteger(lm) || lm <= 0 || (sessionMinutes > 0 && lm >= sessionMinutes)) {
            setError(t('teacherAttendancePage.errors.invalidLateMinutes', { studentId: sid, max: Math.max(sessionMinutes - 1, 1) }));
            setSaving(false);
            return;
          }
        }
      }

      const attendanceRecords = Object.entries(attendance).map(([studentId, status]) => ({
        studentId: Number(studentId),
        status,
        lateMinutes: status === ATTENDANCE_STATUS.LATE ? Number(lateMinutesMap[studentId]) : 0,
      }));
      const payload = { classId: selectedClass, subjectId: Number(selectedSubject), date: dateStr, periods: Number(periods) || 1, period: resolvedPeriod, attendanceRecords };

      // ONE POSITION PER REGISTER, and the key is OMITTED rather than sent as
      // nulls when there is no reading. To the server, absent and empty have to
      // mean the same thing — a teacher whose GPS cannot get a fix, or a desktop
      // with location denied, is recorded as `unknown` and allowed through.
      if (geo.fix) {
        payload.position = {
          latitude: geo.fix.latitude,
          longitude: geo.fix.longitude,
          accuracy: Math.round(geo.fix.accuracy || 0),
          samples: geo.fix.samples,
          spread: geo.fix.spread,
          source: geo.fix.source,
          capturedAt: new Date(geo.fix.capturedAt).toISOString(),
        };
      }

      // Sent directly, NOT through the offline outbox.
      //
      // The server re-validates the clock when the request ARRIVES, not when it
      // was composed: assertDateIsToday refuses any date but today, and
      // resolveTimetableSlotForAttendance requires `now` to fall inside the
      // lesson plus ten minutes' grace. A queued register therefore cannot
      // succeed once the lesson has passed.
      //
      // Queueing it was worse than refusing: the teacher saw "will be sent when
      // you reconnect" and walked away, while the outbox retried and was
      // rejected every time. Its 409 branch means "the server is still
      // processing this replay, keep it", which is exactly the status an
      // out-of-window register returns — so it would never be delivered and
      // never discarded either.
      //
      // The idempotency key is retained for the case the outbox exists for: the
      // request committed but the response was lost, so a manual retry cannot
      // file the register twice.
      await fetchJson('/api/attendance/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': newIdempotencyKey(),
        },
        body: JSON.stringify(payload),
      });
      setError(null);
      setSuccessMessage(t('teacherAttendancePage.messages.attendanceSavedSuccess', 'Attendance saved successfully!'));
    } catch (err) {
      console.error('Failed to save attendance:', err);
      setSuccessMessage(null);
      // The server measures the distance itself now, and its refusal carries the
      // figures. Composing from them rather than falling through to the generic
      // handler is what turns "could not save" into a sentence a teacher can act
      // on. (getRequestErrorMessage would in fact surface `err.data.error` here,
      // but stating the branch means the numbers cannot be lost to a future
      // change in that helper.)
      if (err?.data?.reason === 'outside_geofence') {
        setError(t('teacherAttendancePage.errors.geoOutside', {
          distance: err.data.distance ?? '?',
          radius: err.data.radius ?? DEFAULT_GEOFENCE_RADIUS,
        }));
      } else {
        setError(getRequestErrorMessage(err, t('teacherAttendancePage.errors.saveFailed')));
      }
    } finally {
      setSaving(false);
    }
  };

  const loadAttendanceHistory = async () => {
    if (!selectedClass) return;
    try {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      const startStr = toLocalDateOnly(start);
      const endStr = toLocalDateOnly(end);
      const qs = new URLSearchParams({ classId: selectedClass, startDate: startStr, endDate: endStr });
      if (selectedSubject) qs.set('subjectId', selectedSubject);
      const summary = await fetchJson(`/api/attendance/summary?${qs.toString()}`);
      const daily = summary?.dailyData || summary?.dailyDataArray || [];
      setAttendanceHistory(daily);
    } catch (err) {
      console.error('Failed to load attendance history:', err);
      setAttendanceHistory([]);
    }
  };

  const getAttendanceStats = () => {
    const stats = { present: 0, absent: 0, late: 0 };
    Object.values(attendance).forEach(status => {
      if (stats.hasOwnProperty(status)) {
        stats[status]++;
      }
    });
    return stats;
  };

  const stats = getAttendanceStats();
  const totalStudents = students.length;
  const attendanceRate = totalStudents > 0 ? ((stats.present + stats.late) / totalStudents) * 100 : 0;

  /* -- the location panel --------------------------------------------------
   *
   * Byte-for-byte the same three rows the phone shows, in the same order: the
   * school's saved pin, the teacher's live reading, and the distance between
   * them. Both coordinate pairs in full.
   *
   * Everything here used to be inside a hover tooltip, which meant it did not
   * exist on a touch device — so a teacher who was refused saw a red icon and
   * no figures at all, and could not tell anyone what the app had actually
   * measured.
   */
  const geoTone = geoStatus === 'inside' ? 'success.main'
    : geoStatus === 'uncertain' ? 'warning.main'
      : geoStatus === 'outside' || geoStatus === 'error' || geoStatus === 'unavailable' ? 'error.main'
        : 'text.secondary';

  const geoHeadline = {
    inside: t('teacherAttendancePage.geo.inside', { defaultValue: 'Within school range' }),
    uncertain: t('teacherAttendancePage.geo.uncertain', { defaultValue: 'Weak GPS signal' }),
    outside: t('teacherAttendancePage.geo.outside', { defaultValue: 'Outside school range' }),
    checking: t('teacherAttendancePage.geo.checking', { defaultValue: 'Checking location…' }),
    unavailable: t('teacherAttendancePage.geo.unavailable', { defaultValue: 'This browser cannot report a location' }),
    error: t('teacherAttendancePage.geo.error', { defaultValue: 'No location could be read' }),
    idle: t('teacherAttendancePage.geo.idle', { defaultValue: 'Location not checked' }),
  }[geoStatus] || '';

  const geoPanel = schoolGeo?.enforceGeofence ? (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ py: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <GpsFixedIcon fontSize="small" sx={{ color: geoTone }} />
          <Typography variant="subtitle2" sx={{ color: geoTone, flexGrow: 1 }}>
            {geoHeadline}
          </Typography>
          <Button size="small" onClick={measure} disabled={geoStatus === 'checking'}>
            {t('teacherAttendancePage.geo.recheck', { defaultValue: 'Check again' })}
          </Button>
        </Box>

        {/* THE TWO COORDINATE PAIRS AND THE DISTANCE USED TO BE PRINTED HERE.
            They were a diagnostic, added to answer "why does it say I am a
            kilometre away", and they answered it — the school's pin turned out
            to have been saved from a ±100m reading. Once that is fixed the rows
            are three lines of raw numbers on a page a teacher opens forty times
            a term, so they are gone. The verdict above still says which side of
            the fence the reading puts them on, the warning below still says when
            the pin itself is the problem, and Check again still re-measures. */}

        {/* THE PIN ITSELF MAY BE THE PROBLEM, and until now nothing said so.
            When the school's saved point came from a poor reading, the distance
            above is wrong by about that much — and a teacher told only "you are
            1200m away" ends up arguing with the app about their own position,
            which was never the thing that was wrong.

            Shown ONLY when the server says the pin is untrustworthy. `null`
            means the pin predates provenance being recorded, which is not
            evidence of anything a teacher can act on, and a warning on every
            register trains people to stop reading warnings. */}
        {schoolGeo.pinTrusted === false && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {t('teacherAttendancePage.pin.untrusted', {
              accuracy: schoolGeo.pinAccuracy ?? '?',
              defaultValue: 'This school’s saved location was recorded from a ±{{accuracy}} m reading, so the distance above can be wrong by about that much. You can still take the register. Ask an administrator to set the school location again from the campus.',
            })}
          </Alert>
        )}
      </CardContent>
    </Card>
  ) : null;

  if (loading) {
    return (
      <Box p={2}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box p={2}>
      <Card>
        <CardHeader
          title={t('teacherAttendancePage.title')}
          subheader={t('teacherAttendancePage.subtitle')}
          action={
            <Box display="flex" gap={1}>
              <Tooltip title={t('common.refresh')}>
                <IconButton onClick={loadClasses}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('teacherAttendancePage.actions.history')}>
                <IconButton onClick={() => {
                  loadAttendanceHistory();
                  setShowHistoryDialog(true);
                }}>
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />
        <CardContent>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Class, Subject and Date Selection */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{t('common.selectClass')}</InputLabel>
                <Select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  label={t('common.selectClass')}
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls.id} value={cls.id}>
                      {(cls.name || '').replace(/\s*-\s*N\/A\s*$/i, '').trim() || String(cls.id)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth disabled={!selectedClass}>
                <InputLabel>{t('teacherAttendancePage.fields.selectSubject')}</InputLabel>
                <Select
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  label={t('teacherAttendancePage.fields.selectSubject')}
                >
                  {subjects.map((subj) => (
                    <MenuItem key={subj.id} value={subj.id}>
                      {subj.name || t('common.na')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t('teacherAttendancePage.fields.selectDate')}
                  value={selectedDate}
                  onChange={setSelectedDate}
                  shouldDisableDate={(date) => {
                    if (!date || !allowedDaysSet.size) return false;
                    const jsDay = date.getDay();
                    const dayNum = jsDay === 0 ? 7 : jsDay;
                    const dayName = getWeekdayName(date);
                    return !allowedDaysSet.has(String(dayNum)) && !allowedDaysSet.has(String(dayName));
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
              <Typography variant="caption" color={isSameDaySelection ? 'success.main' : 'warning.main'}>
                {isSameDaySelection
                  ? t('teacherAttendancePage.hints.todayValid')
                  : t('teacherAttendancePage.hints.onlyToday')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('teacherAttendancePage.fields.classDuration')}
                fullWidth
                value={durationMinutes ? t('teacherAttendancePage.fields.durationValue', { hours: Math.floor(durationMinutes / 60), minutes: durationMinutes % 60 }) : ''}
                disabled
              />
              <Typography variant="caption" color="text.secondary">
                {resolvedPeriod ? t('teacherAttendancePage.fields.sessionWithValue', { value: resolvedPeriod }) : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label={t('teacherAttendancePage.fields.evaluation', { defaultValue: 'Evaluation' })}
                fullWidth
                value={currentEvaluation
                  ? (currentEvaluation.name
                    || (currentEvaluation.sequenceNo
                      ? `${t('teacherAttendancePage.fields.sequenceLabel', { defaultValue: 'Sequence' })} ${currentEvaluation.sequenceNo}`
                      : currentEvaluation.type || ''))
                  : t('common.none', { defaultValue: 'None' })}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveAttendance}
                  disabled={saving || !selectedClass || !selectedSubject || !canRecordAttendance}
                  fullWidth
                >
                  {saving ? t('common.saving') : t('teacherAttendancePage.actions.saveAttendance')}
                </Button>
                {schoolGeo?.enforceGeofence && (
                  // A colour-coded affordance only. The NUMBERS moved out of a
                  // tooltip and into the panel below: a tooltip is hover-only,
                  // and teachers take registers on touch screens where hover
                  // does not exist — so the figures the decision was made from
                  // were, in practice, invisible to everyone who needed them.
                  <GpsFixedIcon
                    titleAccess={t('teacherAttendancePage.hints.geofenceActive')}
                    color={
                      geoStatus === 'inside' ? 'success'
                        : geoStatus === 'uncertain' ? 'warning'
                          : geoStatus === 'outside' ? 'error' : 'action'
                    }
                    sx={{ ml: 1, alignSelf: 'center' }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          {/* WHERE THE SCHOOL IS, AND WHERE YOU ARE — both, in full.
              A distance on its own cannot distinguish a teacher in the wrong
              place from a school pinned in the wrong place, and that ambiguity
              is what left "I set the school location and it says I am 1km away"
              unanswerable for so long. Six decimal places is about 0.1m, so the
              two lines can simply be read and compared. */}
          {geoPanel}

          {selectedClass && selectedSubject && isSameDaySelection && !sessionWindow && !sessionOutsideWindow && sessionResolveError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No timetable slot for this subject at this time.
            </Alert>
          )}

        {/* Attendance Table */}
        {selectedClass && selectedSubject && isSameDaySelection && sessionWindow && students.length > 0 && !sessionResolveError && (
          <Card variant="outlined">
            <CardHeader
              title={t('teacherAttendancePage.table.titleWithClass', { name: classes.find(c => c.id === selectedClass)?.name || '' })}
              subheader={t('teacherAttendancePage.table.dateWithValue', { date: selectedDate.toLocaleDateString(undefined, { dateStyle: 'medium' }) })}
            />
            <CardContent>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.name')}</TableCell>
                    <TableCell align="center">{t('teacherAttendancePage.status.present')}</TableCell>
                    <TableCell align="center">{t('teacherAttendancePage.status.absent')}</TableCell>
                    <TableCell align="center">{t('teacherAttendancePage.status.late')}</TableCell>
                    <TableCell>{t('teacherAttendancePage.fields.lateMinutes')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {students.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell>{`${student.firstName || ''} ${student.lastName || ''}`.trim() || t('common.studentNameFallback')}</TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={attendance[student.id] === ATTENDANCE_STATUS.PRESENT}
                          onChange={() => handleAttendanceChange(student.id, ATTENDANCE_STATUS.PRESENT)}
                          color="success"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={attendance[student.id] === ATTENDANCE_STATUS.ABSENT}
                          onChange={() => handleAttendanceChange(student.id, ATTENDANCE_STATUS.ABSENT)}
                          color="error"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={attendance[student.id] === ATTENDANCE_STATUS.LATE}
                          onChange={() => handleAttendanceChange(student.id, ATTENDANCE_STATUS.LATE)}
                          color="warning"
                        />
                      </TableCell>
                      <TableCell>
                        {attendance[student.id] === ATTENDANCE_STATUS.LATE ? (
                          <TextField
                            type="number"
                            size="small"
                            value={lateMinutesMap[student.id] ?? ''}
                            onChange={(e) => handleLateMinutesChange(student.id, e.target.value)}
                            inputProps={{ min: 1, max: Math.max((Number(periods) || 0) * 50 - 1, 1) }}
                            placeholder={t('teacherAttendancePage.fields.minutes')}
                            sx={{ maxWidth: 120 }}
                          />
                        ) : (
                          <Chip
                            label={attendance[student.id] ? t(`teacherAttendancePage.status.${attendance[student.id]}`) : t('teacherAttendancePage.status.present')}
                            color={STATUS_COLORS[attendance[student.id]] || 'default'}
                            size="small"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                </Table>
              </CardContent>
          </Card>
        )}

        {!selectedClass && (
          <Alert severity="info">
            {t('teacherAttendancePage.hints.selectClass')}
          </Alert>
        )}
      </CardContent>
    </Card>

      {/* Attendance History Dialog */}
      <Dialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('teacherAttendancePage.dialogs.history.title')}</DialogTitle>
        <DialogContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('teacherAttendancePage.fields.date')}</TableCell>
                <TableCell align="right">{t('teacherAttendancePage.status.present')}</TableCell>
                <TableCell align="right">{t('teacherAttendancePage.status.absent')}</TableCell>
                <TableCell align="right">{t('teacherAttendancePage.status.late')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {attendanceHistory.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {t('teacherAttendancePage.dialogs.history.noRecords', 'No attendance records found for the last 30 days.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {attendanceHistory.map((record) => (
                <TableRow key={record.date}>
                  <TableCell>{record.date}</TableCell>
                  <TableCell align="right">{record.present}</TableCell>
                  <TableCell align="right">{record.absent}</TableCell>
                  <TableCell align="right">{record.late}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistoryDialog(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 